import { DatePicker } from "@app/components/DatePicker.tsx";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useRosterData from "@app/data-hooks/use-roster-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { CurrencyInput, Dialog } from "bi-lib";
import moment from "moment";

interface Props {
    open: boolean;
    rosterId?: number;
    rosterDetailId?: number;
    onClose: () => void;
}

export default function AssignmentRosterDetailModal({ open, rosterId, rosterDetailId, onClose }: Props) {
    const { rosterDetail } = useAssignmentData({ rosterDetailId: open ? rosterDetailId : undefined });
    const { saveRosterDetail } = useRosterData();
    const { staff } = useStaffData({ id: rosterDetail.data?.staffId });

    const defaultValues: any = {
        effectiveDate: rosterDetail.data?.effectiveDate ? moment(rosterDetail.data?.effectiveDate) : null,
        billableTime: rosterDetail.data?.billableTime,
        billableTravelExpense: rosterDetail.data?.billableTravelExpense,
        billableOtherIndividualExpense: rosterDetail.data?.billableOtherIndividualExpense,
        billableOfficeExpense: rosterDetail.data?.billableOfficeExpense,
        invoiceVisibleTime: rosterDetail.data?.invoiceVisibleTime,
        invoiceVisibleTravelExpense: rosterDetail.data?.invoiceVisibleTravelExpense,
        invoiceVisibleOfficeExpense: rosterDetail.data?.invoiceVisibleOfficeExpense,
        timeReimbursable: rosterDetail.data?.timeReimbursable,
        costRate: rosterDetail.data?.costRate,
        billingRate: rosterDetail.data?.billingRate,
        internalRate: rosterDetail.data?.internalRate,
        incentiveRate: rosterDetail.data?.incentiveRate,
    };
    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            await saveRosterDetail.mutateAsync({
                rosterId,
                rosterDetailId,
                effectiveDate: value.effectiveDate?.format("MM/DD/YYYY"),
                billableTime: value.billableTime,
                billableTravelExpense: value.billableTravelExpense,
                billableOtherIndividualExpense: value.billableOtherIndividualExpense,
                billableOfficeExpense: value.billableOfficeExpense,
                invoiceVisibleTime: value.invoiceVisibleTime,
                invoiceVisibleTravelExpense: value.invoiceVisibleTravelExpense,
                invoiceVisibleOfficeExpense: value.invoiceVisibleOfficeExpense,
                timeReimbursable: value.timeReimbursable,
                costRate: value.costRate,
                billingRate: value.billingRate,
                internalRate: value.internalRate,
                incentiveRate: value.incentiveRate,
            });
            onClose();
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
            <DialogTitle>
                {rosterDetailId == undefined ? "Create" : "Edit"}
                {staff.data != undefined ? ` ${staff.data.fullName}` : undefined} Roster Detail
            </DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Stack spacing={1}>
                        <form.Field name={"effectiveDate"}>
                            {(field) => (
                                <DatePicker
                                    label={"Effective Date"}
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange(e)}
                                    required
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billableTime"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Billable Time"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billableTravelExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Billable Travel Expenses"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billableOtherIndividualExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Billable Other Expenses"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billableOfficeExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Billable Office Expenses"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"invoiceVisibleTime"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Invoice Visible Time"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"invoiceVisibleTravelExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Invoice Visible Travel Expenses"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billableOtherIndividualExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Invoice Visible Other Expenses"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"invoiceVisibleOfficeExpense"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Invoice Visible Office Expenses"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"timeReimbursable"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Time Reimbursable"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"costRate"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Cost Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"billingRate"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Billing Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"internalRate"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Internal Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"incentiveRate"}>
                            {(field) => (
                                <CurrencyInput
                                    type={"number"}
                                    label={"Incentive Rate"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e)}
                                />
                            )}
                        </form.Field>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={onClose}>
                        Close
                    </Button>
                    <Button type={"submit"} variant={"contained"}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
